import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useSoundEffects } from '../../hooks/useSoundEffects';
import { useHaptic } from '../../hooks/useHaptic';
import { useOnboardingStore } from '../../hooks/useOnboardingStore';
import { useChildStore } from '../../hooks/useChildStore';
import AnimatedButton from '../../components/ui/AnimatedButton';
import MindoraCharacter from '../../components/character/MindoraCharacter';
import SpeechBubble from '../../components/ui/SpeechBubble';
import { INTERESTS, interestsToSubjects } from './interests';

interface Props {
  onNext: () => void;
  onBack: () => void;
}

export default function ChildInterestsStep({ onNext }: Props) {
  const { childName, childAge, childInterests, toggleInterest, setCreatedChildId } =
    useOnboardingStore();
  const { addChild } = useChildStore();
  const { play } = useSoundEffects();
  const haptic = useHaptic();
  const reduced = useReducedMotion();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const handleToggle = (id: string) => {
    const wasSelected = childInterests.includes(id);
    toggleInterest(id);
    play(wasSelected ? 'tap' : 'taskChime');
    haptic.tap();
  };

  const handleCreate = async () => {
    if (!childAge) return;
    setIsCreating(true);
    setError('');

    try {
      const currentYear = new Date().getFullYear();
      const dateOfBirth = new Date(currentYear - childAge, 5, 15)
        .toISOString()
        .split('T')[0];
      const gradeLevel = Math.min(Math.max(childAge - 5, 0), 12);
      const allowedSubjects = interestsToSubjects(childInterests);

      const child = await addChild({
        name: childName.trim(),
        dateOfBirth,
        gradeLevel,
        allowedSubjects,
        parentalConsent: true,
      });

      setCreatedChildId(child.id);
      onNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create profile. Please try again.');
      setIsCreating(false);
    }
  };

  const canProceed = childInterests.length >= 1;
  // Companion celebrates at 3+ interests
  const companionMood = childInterests.length >= 3 ? 'celebrating' : 'happy';

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 py-12">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={reduced ? { duration: 0.3 } : springs.gentle}
      >
        <MindoraCharacter mood={companionMood} size="lg" />
      </motion.div>

      <motion.div
        className="mt-4 mb-6 max-w-xs w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduced ? { duration: 0.3, delay: 0.1 } : { ...springs.gentle, delay: 0.2 }}
      >
        <SpeechBubble>
          <p className="font-display text-kid-xl text-kid-text text-center">
            What do you like, {childName}? Tap as many as you want!
          </p>
        </SpeechBubble>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-4 p-3 bg-kid-coral/15 border-2 border-kid-coral/30 text-kid-text rounded-kid text-sm font-kid max-w-md"
        >
          {error}
        </motion.div>
      )}

      {/* Circular bouncing bubbles */}
      <div className="flex flex-wrap justify-center gap-3 max-w-md mb-6">
        {INTERESTS.map((interest) => {
          const selected = childInterests.includes(interest.id);
          return (
            <motion.button
              key={interest.id}
              onClick={() => handleToggle(interest.id)}
              className={`w-24 h-24 rounded-full flex flex-col items-center justify-center gap-1 transition-all border-3 touch-target ${
                selected
                  ? 'shadow-kid-lg border-transparent'
                  : 'bg-kid-surface border-kid-border shadow-kid hover:scale-105'
              }`}
              style={selected ? {
                backgroundColor: interest.color,
                borderColor: interest.color,
              } : undefined}
              // Multi-keyframe bounce on selection
              animate={selected && !reduced ? {
                scale: [1, 1.2, 0.9, 1.1, 1],
                rotate: [0, -5, 5, -2, 0],
              } : {}}
              transition={springs.wobbly}
              whileTap={{ scale: 0.9 }}
            >
              <span className={`text-4xl ${selected ? '' : ''}`}>
                {interest.emoji}
              </span>
              <span className={`text-[10px] font-kid font-bold leading-tight ${
                selected ? 'text-white' : 'text-kid-text'
              }`}>
                {interest.label}
              </span>
            </motion.button>
          );
        })}
      </div>

      {/* COPPA consent text */}
      <p className="text-[10px] text-kid-text-soft font-kid text-center max-w-sm mb-4 leading-relaxed">
        By continuing, you confirm you have parental authority and consent to create this child profile in accordance with COPPA guidelines.
      </p>

      <AnimatePresence>
        {canProceed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={reduced ? { duration: 0.2 } : springs.gentle}
          >
            <AnimatedButton
              variant="marshmallow"
              className="btn-marshmallow-ocean"
              onClick={handleCreate}
              disabled={isCreating}
            >
              {isCreating ? 'Setting up...' : 'Meet your buddy!'}
            </AnimatedButton>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
